import React from 'react'

import { I18n, Translate } from 'react-redux-i18n'
import { CloseBondDetails } from '../actions/bondActions'
import { notifyParentSignupRequired } from '../utils/common-utils'
import moment from 'moment'
import {
  calculateEffectiveYield,
  calculateNextPaymentDate,
  BondNextCallInfo,
  BondTextSection,
  BondConfigItem,
} from '@bdswiss/mt4-connector'
import BondTradingChart from '../containers/BondTradingChart'
import HelpTooltip from './HelpTooltip'

export interface Props {
  positionsPaneOpened: boolean
  selectedBond?: BondConfigItem
  locale: string
  mode: string
  demo?: string
  onCloseBondDetails: () => CloseBondDetails
}

export interface State {
  chartOpen: boolean
}

class BondsDetailView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.toggle = this.toggle.bind(this)
    this.state = { chartOpen: false }
  }

  public render() {
    const positionsPaneOpened = this.props.positionsPaneOpened
    const bond = this.props.selectedBond!
    const effectiveYield = calculateEffectiveYield(bond, 10000).toFixed(2)
    const nextPaymentDate = calculateNextPaymentDate(bond)
    moment.locale(this.props.locale)
    const nextPaymentDateFormatted = nextPaymentDate ? moment(nextPaymentDate).format('L') : ''
    const maturityDateFormatted = bond.maturityDate
      ? moment(bond.maturityDate).format('L')
      : I18n.t('perpetual')

    // the green buy button is present on bond detail page only in bonds demo embedded mode
    let greenBuyButton
    if (this.props.mode === 'embedded' && this.props.demo === 'bonds') {
      greenBuyButton = (
        <div className="row">
          <div className="col-lg-4 d-none d-lg-block">&nbsp;</div>
          <div className="mb-5 col-lg-8 col-sm-12">
            <div className="green-buy-button-container">
              <button
                onClick={() => notifyParentSignupRequired()}
                className="btn btn-success col"
                data-cy="placeTrade"
              >
                <Translate value={'buy'} />
              </button>
              <div className="color-light-gray">
                <Translate value={'yourCapitalIsAtRisk'} />
              </div>
            </div>
          </div>
        </div>
      )
    } else {
      greenBuyButton = <div />
    }

    return (
      <div className="bond-detail d-flex flex-column no-scrolling">
        <div className="hidden-wrapper__header d-flex justify-content-start">
          <button
            onClick={() => this.props.onCloseBondDetails()}
            className="btn btn-with-icon"
            data-cy="backToBondList"
          >
            <i className="icon-back align-middle" />
            <Translate value="backToBondList" />
          </button>
        </div>
        <div className="bond-detail__wrapper container-fluid">
          <div className="bond-detail__header_wrapper">
            <div className="bond-detail__logo_wrapper">
              <a href={bond.websiteUrl} target="_blank" rel="noopener noreferrer">
                <img src={bond.logoUrl} alt="" className="bond-detail__logo" />
              </a>
            </div>
            <div className="row mb-5">
              <div
                className={
                  (positionsPaneOpened ? 'col-xl-12' : 'col-xxl-3') +
                  ' col-lg-5 col-md-5 col-6 bond-detail__title-block border-left mb-3'
                }
              >
                <div className="font-weight-medium fz-24 mb-2 title">{bond.name}</div>
                <div className="font-weight-normal fz-14">{bond.establishedYear}</div>
                <div className="font-weight-normal fz-14">{bond.ownershipType}</div>
              </div>
              <div
                className={
                  (positionsPaneOpened ? 'col-xl-12' : 'col-xxl-4') + ' col-md-7 col-12 mb-3'
                }
              >
                <div className="bond-detail__leveraged-interest-block d-flex">
                  <div className="d-flex row align-items-center">
                    <div className="col-6 fz-15 align-self-center label">
                      <Translate value={'leveragedInterest'} />
                      <HelpTooltip content="bondNetInterestDescription" />
                    </div>
                    <div className="col-6 align-self-center value">{effectiveYield}%</div>
                  </div>
                </div>
              </div>
              <div
                className={
                  (positionsPaneOpened ? 'col-xl-12' : 'col-xxl-5 mt-xl-0') +
                  ' d-flex col-lg-12 mb-3'
                }
              >
                <div
                  className={
                    bond.status === 'Performing'
                      ? 'd-lg-flex flex-fill align-self-center bond-detail__status-block bond-detail__info'
                      : 'd-lg-flex flex-fill align-self-center bond-detail__status-block bond-detail__warn'
                  }
                >
                  <div className="d-lg-flex row align-self-stretch ">
                    <div className="col-4 align-self-center">
                      <span className="checkmark">
                        <i className="icon-tick">&nbsp;</i>
                      </span>
                    </div>
                    <div className="col-8 align-self-center">
                      <div>
                        <Translate value={'status'} />
                      </div>
                      <div className="status-name font-weight-medium fz-20">{bond.status}</div>
                      <div className="sidebar__label">
                        {bond.status === 'Performing' ? I18n.t('allPaymentsOnTime') : ''}
                      </div>
                    </div>
                    {/*                                        <div className="col-md-6 align-self-center d-none ">
                                            <div className="sidebar__label">{bond.status==='Performing' ? I18n.t('allPaymentsOnTime') : ""}</div>
                                            <div className="sidebar__label">{bond.status==='Performing' ? I18n.t('allCoveandsMet') : ""}</div>
                                        </div>*/}
                  </div>
                </div>
              </div>
            </div>

            {greenBuyButton}
          </div>

          <div className="mb-3 bond-detail__content">
            <div className="row">
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'ISIN'} />
                </div>
                <div className="font-weight-medium">{bond.isin}</div>
              </div>
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'coupon'} />
                </div>
                <div className="font-weight-medium">{bond.couponInterestRate}%</div>
              </div>
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'maturityDate'} />
                </div>
                <div className="font-weight-medium">{maturityDateFormatted}</div>
              </div>

              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'interestPaymentFrequency'} />
                </div>
                <div className="font-weight-medium">{bond.interestPaymentFrequency}m</div>
              </div>
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'nextInterestPayment'} />
                </div>
                <div className="font-weight-medium">{nextPaymentDateFormatted}</div>
              </div>
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'assetSecurity'} />
                </div>
                <div className="font-weight-medium">{bond.assetSecurity}</div>
              </div>
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'industryType'} />
                </div>
                <div className="font-weight-medium">{bond.industryType}</div>
              </div>
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="sidebar__label">
                  <Translate value={'nextCall'} />
                </div>
                {bond.nextCallInfo == null ? (
                  <div className="font-weight-medium">N/A</div>
                ) : (
                  bond.nextCallInfo.map((item, index) => (
                    <NextCallItem item={item} key={index} locale={this.props.locale} />
                  ))
                )}
              </div>
            </div>
            <div className="row mt-lg-4 mb-lg-5">
              <div className="col-lg-12 border-left font-weight-normal">
                <div className="font-weight-medium ">
                  <Translate value={'businessDescription'} />
                </div>
                <p className="fz-14 color-33 ">{bond.businessDescriptionLong}</p>
              </div>
            </div>
          </div>

          {bond.textSections && bond.textSections.map
            ? bond.textSections.map((item, index) => <TextSection item={item} key={index} />)
            : ''}
        </div>
        <div className="row d-none d-md-flex">
          <div className="col-lg-2 d-none d-lg-block">&nbsp;</div>
          <div className="col-lg-10">
            <div
              className={
                'flex-column ' +
                (this.state.chartOpen ? 'chart-collapser-open' : 'chart-collapser-closed')
              }
            >
              <div className="chart-collapser-header d-flex justify-content-end">
                <button onClick={this.toggle} className="btn btn-with-icon" data-cy="showChart">
                  <i
                    className={
                      'align-middle ' +
                      (this.state.chartOpen ? 'icon-triangle-down' : 'icon-triangle-up')
                    }
                  />
                  <Translate value={this.state.chartOpen ? 'hideChart' : 'showChart'} />
                </button>
              </div>
              <BondTradingChart />
            </div>
          </div>
        </div>
      </div>
    )
  }

  private toggle() {
    this.setState(prevState => ({
      chartOpen: !prevState.chartOpen,
    }))
  }
}

export default BondsDetailView

interface NextCallItemProps {
  item: BondNextCallInfo
  key: number
  locale: string
}
const NextCallItem = ({ item, key, locale }: NextCallItemProps) => {
  moment.locale(locale)
  return (
    <div className="font-weight-medium" key={key}>
      {moment(item.date).format('L')}@{item.price}
    </div>
  )
}

interface TextSectionProps {
  item: BondTextSection
  key: number
}
const TextSection = ({ item, key }: TextSectionProps) => {
  return (
    <div className="textSectionWrapper" key={key}>
      <div className="row">
        <div className="col-12">
          <div className="font-weight-medium">{item.title}</div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <p className="fz-14 color-33">{item.htmlText}</p>
        </div>
      </div>
    </div>
  )
}
